<template>
  <div class="analytics-controls-container">
    <div class="analytics-controls" :class="{ advanced: type === AnalyticsType.ADVANCED }">
      <template v-if="type === AnalyticsType.ADVANCED">
        <AnalyticsAccountSelector class="analytics-controls__account-selector" />
        <AnalyticsCompanySelector class="analytics-controls__company-selector" />
        <AnalyticsServiceSelector class="analytics-controls__service-selector" />
      </template>
      <RadioGroup
        v-model="selectedOptionProxy"
        :options="radioOptions"
        class="analytics-controls__radio-group"
      />
    </div>
    <DatePicker
      :title="$t('data_picker.title')"
      :key="analyticsDateRange.date_from"
      :startPeriod="analyticsDateRange.date_from"
      :endPeriod="analyticsDateRange.date_to"
      :immediateUpdate="false"
      :showIconArrow="true"
      :opens="datePickerOpens"
      class="analytics-controls__date-picker"
      width=""
      @changeDates="changeDate"
    />
  </div>
</template>

<script>
import RadioGroup from '@/components/elements/RadioGroup';
import DatePicker from '@/components/sort/DatePicker';
import AnalyticsAccountSelector from '@/views/Analytics/AnalyticsSelectors/AnalyticsAccountSelector';
import AnalyticsCompanySelector from '@/views/Analytics/AnalyticsSelectors/AnalyticsCompanySelector';
import AnalyticsServiceSelector from '@/views/Analytics/AnalyticsSelectors/AnalyticsServiceSelector';
import { bindScreenResize } from '@/utils/screen';
import { KPIKeys, KPIText, AnalyticsType } from '@/utils/common';
import moment from 'moment';

export default {
  name: 'AnalyticsControls',
  components: {
    RadioGroup,
    DatePicker,
    AnalyticsAccountSelector,
    AnalyticsCompanySelector,
    AnalyticsServiceSelector,
  },
  props: {
    type: {
      type: String,
      validator: function (value) {
        return Object.values(AnalyticsType).includes(value);
      },
    },
  },
  data() {
    return {
      AnalyticsType,
      KPIKeys,
      KPIText,
      isMobileScreen: false,
      disposeScreenResize: null,
      radioOptions: Object.keys(KPIKeys).map((key) => ({
        label: KPIText[key],
        value: KPIKeys[key],
      })),
    };
  },
  computed: {
    analyticsSelectedOption() {
      return this.$store.state.Analytics[this.type].selectedOption;
    },
    analyticsDateRange() {
      return this.$store.state.Analytics.dateRange;
    },
    datePickerOpens() {
      return this.isMobileScreen ? 'right' : 'left';
    },
    selectedOptionProxy: {
      get() {
        return this.analyticsSelectedOption;
      },

      set(val) {
        this.$store.commit('Analytics/changeAnalyticsSelectedOption', {
          value: val,
          type: this.type,
        });
      },
    },
  },
  methods: {
    getMobileBreakpoint() {
      return 768;
    },
    changeDate(val) {
      this.$store.commit('Analytics/changeAnalyticsDateRange', {
        date_from: moment(val.startDate).format('YYYY-MM-DD'),
        date_to: moment(val.endDate).format('YYYY-MM-DD'),
      });
    },
  },
  mounted() {
    this.disposeScreenResize = bindScreenResize(this, () => this.getMobileBreakpoint());
  },
  beforeDestroy() {
    if (this.disposeScreenResize) {
      this.disposeScreenResize();
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/helpers/variables';
@import '@/assets/styles/helpers/mixin';

.analytics-controls-container {
  position: relative;
  --analytics-controls-datepicker-top: -60px;
}

.analytics-controls {
  display: grid;
  grid-template-areas: 'radiogroup radiogroup radiogroup';
  grid-template-columns: 1fr 0;
  grid-gap: 15px 6px;
  align-items: center;
  background: $basicWhite;
  padding: 20px;
  border-radius: 4px;
  position: relative;

  @include below(768px) {
    padding: 0;
    background: transparent;
    grid-template-areas:
      'radiogroup'
      'datepicker';
    grid-template-columns: 1fr;
  }

  &.advanced {
    grid-template-areas:
      'service account company'
      'radiogroup radiogroup radiogroup';
    grid-template-columns: 1fr 1fr 1fr;

    @include below(768px) {
      padding: 0;
      background: transparent;
      grid-template-areas:
        'service'
        'account'
        'company'
        'radiogroup'
        'datepicker';
      grid-template-columns: 1fr;
    }
  }

  &__service-selector {
    grid-area: service;
  }
  &__account-selector {
    grid-area: account;
  }
  &__company-selector {
    grid-area: company;
  }
  &__radio-group {
    display: flex;
    padding: 15px 0px;
    flex-wrap: wrap;
    gap: 10px 0;
    grid-area: radiogroup;

    @include below(768px) {
      flex-wrap: wrap;
    }
  }
  &__date-picker {
    position: absolute;
    right: 0;
    height: 3.3rem;
    top: var(--analytics-controls-datepicker-top);
    background: $basicWhite;
    grid-area: datepicker;
    border-radius: 0.8rem;

    :deep(.date-picker__icon-arrow-container) {
      display: flex;
      align-items: center;
      top: 0;
      height: 100%;
    }

    @include below(768px) {
      position: relative;
      top: initial;
      left: initial !important;
    }
  }
}
</style>
